<template>
  <content-dialog
    v-if="!!event"
    ref="dialog"
    v-model="dialogOpen"
    v-bind="$attrs"
    fullscreen-mobile
    no-backdrop-dismiss
    width="1150px"
    height="95vh"
    max-height="1000px"
    card-class="flex no-wrap"
    :class="{'appointment-can-peek': enablePeeking, 'is-peeking': enablePeeking && isPeeking}"
    :seamless="isPeeking"
    :card-visible="!!event"
    :loading="checkingAvailability || loading"
    :tags="[helpCodes.createAppointment, helpCodes.scheduler, helpCodes.appointments]"
    @hide="$emit('hide')"
  >
    <template #card>
      <two-panel-split
        with-close
        with-list-toolbar
        list-width="60%"
        list-width-when-viewing="60%"
        toolbar-anchor="none"
        list-classes="flex items-stretch column no-wrap"
        :view-classes="{'flex items-stretch column no-wrap overflow-hidden-x': !!selectedTab}"
        :title="`${modeTitle} Appointment`"
        :is-viewing="!isPeeking && !!selectedTab"
        @close="$refs.dialog.hide()"
        @view-back="selectedTab = null"
      >
        <!-- Slim toolbar to display if dialog is in peek mode -->
        <template v-if="isPeeking" #list-toolbar="{ stickyClasses }">
          <q-toolbar style="z-index: 3;" :class="stickyClasses">
            <q-btn
              flat
              dense
              round
              icon="close"
              @click="$refs.dialog.hide()"
            />

            <q-space />

            <q-btn
              rounded
              no-caps
              color="blue-7"
              label="Expand New Appointment"
              icon="mdi-chevron-up"
              class="absolute-center"
              style="width: 250px;"
              @click="isPeeking = !isPeeking"
            />

            <q-space />
          </q-toolbar>
        </template>

        <template #list-toolbar-inner>
          <q-btn
            v-if="enablePeeking"
            dense
            rounded
            no-caps
            flat
            icon-right="mdi-magnify-expand"
            class="q-mr-sm"
            :label="windowWidth > 450 ? 'Peek' : undefined"
            @click="isPeeking = !isPeeking"
          >
            <q-tooltip>Peek to Calendar</q-tooltip>
          </q-btn>

          <help-button />
        </template>

        <template #list="{ small }">
          <div v-if="!isPeeking" class="flex-auto">
            <q-linear-progress
              v-if="small"
              :value="stepPercentageDone"
              stripe
              class="q-mb-sm"
            />

            <OverviewPartial
              :event="event"
              :name-spacing="170"
              :mode="mode"
              :business-hours="businessHours"
              :errors="errors"
              :selected-tab="selectedTab"
              :can-alter-reminders="canAlterReminders"
              :has-updated-rule="hasUpdatedRule"
              :has-deleted-rule="hasDeletedRule"
              :has-future-rule="hasFutureRule"
              @tab="selectedTab = $event"
              @update-reference="$emit('updateReference')"
            />

            <!-- Details -->
            <AppointmentPartial
              :mode="mode"
              :event="event"
              :errors="errors"
              :business-hours="businessHours"
              :name-spacing="170"
              :has-updated-rule="hasUpdatedRule"
              :has-deleted-rule="hasDeletedRule"
              :has-future-rule="hasFutureRule"
              :selected-tab="selectedTab"
              @tab="selectedTab = $event"
              @update-reference="$emit('updateReference')"
            />
          </div>

          <q-toolbar v-if="!isPeeking" class="sticky-position from-page-bottom blurred-container">
            <q-space />
            <q-btn
              color="green"
              icon-right="check"
              label="Save"
              :loading="checkingAvailability || loading"
              @click="save"
            />
          </q-toolbar>
        </template>

        <template #view="{ back, small }">
          <div class="flex-auto">
            <q-linear-progress
              v-if="small && currentTabIsStepped"
              :value="stepPercentageDone"
              stripe
              class="q-mb-sm"
            />

            <q-tab-panels
              v-model="selectedTab"
              class="overflow-panels"
              :animated="small"
            >
              <!-- Appointment Types -->
              <q-tab-panel name="types" class="q-pa-none">
                <TypesPartial
                  :event="event"
                  :errors="errors"
                  :prefill-date="prefillDate"
                  class="q-mb-lg"
                />
              </q-tab-panel>

              <!-- Clients -->
              <q-tab-panel name="clients" class="q-pa-none">
                <ClientsPartial
                  :event="event"
                  :errors="errors"
                  :without-client="withoutClient"
                  :can-alter-reminders="canAlterReminders"
                  class="q-mb-lg"
                />
              </q-tab-panel>

              <!-- Staff Members -->
              <q-tab-panel name="staff" class="q-pa-none">
                <StaffPartial
                  :mode="mode"
                  :event="event"
                  :errors="errors"
                  :can-alter-reminders="canAlterReminders"
                  class="q-mb-lg"
                  @check-availability="$refs.availabilityDialog.open()"
                />
              </q-tab-panel>

              <!-- Color -->
              <q-tab-panel name="color" class="q-pa-none">
                <ColorPartial
                  :event="event"
                  :errors="errors"
                />
              </q-tab-panel>
            </q-tab-panels>
          </div>

          <q-toolbar
            v-if="small && nextStep"
            class="sticky-position from-page-bottom blurred-container self-end"
          >
            <q-space />
            <q-btn
              v-if="nextStep"
              color="blue"
              icon-right="mdi-arrow-right"
              size="12px"
              :label="nextStep.title"
              @click="selectedTab = nextStep.name"
            />
            <q-space />
          </q-toolbar>
        </template>

        <template v-if="currentTab" #view-toolbar-after="{ small }">
          <page-toolbar-title>
            <template v-if="small" #subtitle>
              {{ modeTitle }} Appointment
            </template>
            {{ currentTab.title }}
          </page-toolbar-title>
          <help-button v-if="small" />
        </template>

        <template v-if="!isPeeking" #view-empty>
          <q-icon
            name="mdi-calendar-edit"
            size="7em"
            color="grey-5"
          />
        </template>
      </two-panel-split>
    </template>
  </content-dialog>

  <AvailabilityDialog
    v-if="!!event"
    :id="event.id"
    ref="availabilityDialog"
    :attendees="event.attendees"
    :start-date="event.start_date"
    :end-date="event.end_date"
    :timezone="event.timezone"
    :is-scheduled="event.status !== 'UNSCHEDULED'"
    :all-day="event.all_day"
    with-editing
    with-suggestions
    @add="baseAddStaff(event, $event)"
    @remove="baseRemoveStaff(event, $event)"
    @continue="confirmSave"
  />
</template>

<script>
import OverviewPartial from './panels/Overview'
import AppointmentPartial from 'components/app/calendar/partials/edit/panels/Appointment'
import ClientsPartial from './panels/Clients'
import StaffPartial from './panels/Staff'
import TypesPartial from './panels/Types.vue'
import ColorPartial from './panels/Color'
import AvailabilityDialog from './../availability/Dialog'

import staffMixin from './../mixins/staffMixin'

export default {
  components: {
    OverviewPartial, AppointmentPartial, ClientsPartial, TypesPartial, StaffPartial, AvailabilityDialog, ColorPartial
  },
  mixins: [staffMixin],
  props: {
    event: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'add'
    },
    withoutClient: {
      type: Boolean,
      default: false
    },
    prefillDate: {
      type: Boolean,
      default: false
    },
    businessHours: {
      type: Object,
      default: null
    },
    hasUpdatedRule: {
      type: Boolean,
      default: false
    },
    hasDeletedRule: {
      type: Boolean,
      default: false
    },
    hasFutureRule: {
      type: Boolean,
      default: false
    },
    // Allow dialog to peek at calendar behind it
    enablePeeking: {
      type: Boolean,
      default: false
    }
  },
  emits: ['hide', 'updateReference', 'save'],
  data () {
    return {
      dialogOpen: false,
      selectedTab: null,
      checkingAvailability: false,
      hasSeenStaffWarning: false,
      isPeeking: false
    }
  },
  computed: {
    modeTitle () {
      return this.mode === 'edit' ? 'Edit' : 'Add'
    },
    tabPanels () {
      return [
        { name: 'overview' },
        { name: 'types', title: 'Appointment Types' },
        { name: 'clients', title: 'Clients & Reminders' },
        { name: 'staff', title: 'Staff & Reminders' },
        { name: 'color', title: 'Colour' },
        { name: null, title: 'More Options' }
      ]
    },
    steppedPanelOrder () {
      return ['types', 'clients', 'staff', null]
    },
    stepPercentageDone () {
      if (!this.selectedTab) {
        return 1
      }
      return (this.steppedPanelOrder.indexOf(this.selectedTab) + 1) / this.steppedPanelOrder.length
    },
    currentTab () {
      return this.tabPanels.find(item => item.name === this.selectedTab)
    },
    currentTabIsStepped () {
      return this.selectedTab && this.steppedPanelOrder.includes(this.selectedTab)
    },
    nextStep () {
      if (!this.selectedTab || !this.steppedPanelOrder.includes(this.selectedTab)) {
        return null
      }
      let tabIndex
      if ((tabIndex = this.steppedPanelOrder.indexOf(this.selectedTab)) >= this.steppedPanelOrder.length) {
        return null
      }
      const nextName = this.steppedPanelOrder[tabIndex + 1]
      return this.tabPanels.find(item => item.name === nextName)
    },
    // Reminders can only be altered if they have a specific appointment status
    // Otherwise don't show the options at all
    canAlterReminders () {
      return this.mode === 'add' || ['UNSCHEDULED', 'UNCONFIRMED', 'SCHEDULED'].includes(this.event.status)
    },
    staffLength () {
      return this.event.attendees.length
    }
  },
  watch: {
    'event.start_date' () {
      this.hasSeenStaffWarning = false
    },
    'event.end_date' () {
      this.hasSeenStaffWarning = false
    },
    'staffLength' () {
      this.hasSeenStaffWarning = false
    }
  },
  methods: {
    open () {
      this.isPeeking = false

      if (this.dialogOpen) {
        return false
      }

      this.dialogOpen = true
      this.selectedTab = this.mode === 'add' ? 'types' : null
      return true
    },
    close () {
      this.dialogOpen = false
    },
    async save () {
      /* Check whether to prompt the user to verify the staff when submitting */
      if (!this.hasSeenStaffWarning && this.event.attendees.length > 0) {
        this.checkingAvailability = true
        const badResults = await this.$refs.availabilityDialog.open(true)
        this.checkingAvailability = false
        if (badResults) {
          this.$refs.availabilityDialog.confirm()
          return
        }
      }
      this.hasSeenStaffWarning = true
      this.confirmSave()
    },
    confirmSave () {
      this.$emit('save')
    }
  }
}
</script>

<style lang="scss">
.q-dialog.appointment-can-peek {
  & > .q-dialog__inner {
    transition: all var(--q-transition-duration) ease;
  }

  &.is-peeking {
    & > .q-dialog__inner {
      top: calc(100% - 50px);

      & > div {
        box-shadow: $shadow-up-5;
      }
    }
  }
}

body.platform-ios {
  &.q-ios-padding {
    .q-dialog.appointment-can-peek {
      &.is-peeking {
        & > .q-dialog__inner {
          padding-top: unset !important;
          padding-bottom: unset !important;
        }
      }
    }
  }

  .q-dialog.appointment-can-peek {
    & > .q-dialog__inner {
      & > div {
        transition: all var(--q-transition-duration);
      }
    }

    &.is-peeking {
      & > .q-dialog__inner {
        top: calc(100% - 50px - var(--safe-area-inset-bottom));

        & > div {
          padding-bottom: var(--safe-area-inset-bottom) !important;
        }

        &.q-dialog__inner--minimized {
          & > div {
            max-height: 100%;
          }
        }
      }
    }
  }
}
</style>
